/**
 * Three-step checkout (woocommerce/checkout/form-checkout.php).
 *
 * - 1 Dane i dostawa → 2 Płatność → 3 Podsumowanie inside one WooCommerce form, so going back never loses data;
 *   steps are kept in the browser history (#krok-2), the back button works,
 * - validation when leaving a field and on "Dalej" - scrolls to the first error, messages are read by screen readers,
 * - phone and postcode masks, city suggestions and the nearest parcel locker for the postcode,
 * - "Edytuj" in the summary opens the step and saving goes straight back to the summary,
 * - mobile bar, double-submit protection, server errors mapped back to their step,
 * - entered data is saved in the session (draft) so it survives going back to the cart.
 */
import {config, debounce, endpoint, post, sprintf, trackEvent} from './shared';

const RETURN_KEY = 'wbsCheckoutReturn';
const RETURN_TTL = 30 * 60 * 1000;

const digits = (value) => String(value || '').replace(/\D+/g, '');

const phoneDigits = (value) => {
    let number = digits(value);
    if (number.length === 11 && number.indexOf('48') === 0) {
        number = number.slice(2);
    } else if (number.length === 13 && number.indexOf('0048') === 0) {
        number = number.slice(4);
    }
    return number;
};

const isNip = (value) => {
    const number = digits(value);
    if (number.length !== 10) {
        return false;
    }
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    const sum = weights.reduce((total, weight, index) => total + weight * Number(number[index]), 0);
    return sum % 11 === Number(number[9]);
};

const RULES = {
    required: (value) => value.trim() !== '',
    email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim()),
    phone: (value) => phoneDigits(value).length === 9,
    postcode: (value) => /^\d{2}-\d{3}$/.test(value.trim()),
    address: (value) => /[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]/.test(value) && /\d/.test(value),
    nip: (value) => isNip(value),
};

const groupDigits = (number) => number.replace(/(\d{3})(?=\d)/g, '$1 ');

const formatPhone = (value) => {
    const international = value.trim().indexOf('+') === 0;
    let number = digits(value);
    if (international && number.indexOf('48') === 0) {
        return `+48 ${groupDigits(number.slice(2, 11))}`.trim();
    }
    return groupDigits(number.slice(0, 9));
};

const formatPostcode = (value) => {
    const number = digits(value).slice(0, 5);
    return number.length > 2 ? `${number.slice(0, 2)}-${number.slice(2)}` : number;
};

/**
 * "POZ04M,Blacharska 2,61-001 Poznań" (InPost map) → "POZ04M - Poznań, Blacharska 2".
 */
const formatInpostLabel = (text, id) => {
    const parts = String(text || '').split(',').map((part) => part.trim()).filter(Boolean);

    if (parts.length >= 3 && parts[0] === id) {
        return `${id} - ${parts[2].replace(/^\d{2}-\d{3}\s*/, '')}, ${parts[1]}`;
    }
    if (text && id && text.indexOf(id) !== 0) {
        return `${id} - ${text}`;
    }
    return text || id;
};

export const CheckoutFlow = () => {
    const root = document.querySelector('[data-wbs-checkout]');
    if (!root || !config()) {
        return;
    }

    const form = root.querySelector('form.checkout');
    const {i18n} = config();
    const $body = $(document.body);
    const byId = (id) => document.getElementById(id);
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const state = {
        step: 1,
        completed: new Set(),
        returnToSummary: false,
        touched: new Set(),
        pointAuto: false,
        pointPostcode: '',
        settingPoint: false,
        busy: false,
    };

    /* Errors ------------------------------------------------------------------------------------ */

    const setError = (input, message) => {
        const row = input.closest('.wbs-field, .form-row');
        const slot = row ? row.querySelector('.wbs-field__error') : null;

        if (row) {
            row.classList.toggle('is-invalid', !!message);
        }
        if (message) {
            input.setAttribute('aria-invalid', 'true');
        } else {
            input.removeAttribute('aria-invalid');
        }
        if (slot && slot.textContent !== message) {
            slot.textContent = message;
        }
    };

    const invoiceOn = () => !!(byId('billing_invoice_checkbox') && byId('billing_invoice_checkbox').checked);
    const invoiceAddressOn = () => invoiceOn() && !!(byId('wbs_invoice_other_address') && byId('wbs_invoice_other_address').checked);

    const isActive = (input) => {
        if (input.dataset.when === 'invoice') {
            return invoiceOn();
        }
        if (input.dataset.when === 'invoice-address') {
            return invoiceAddressOn();
        }
        return true;
    };

    const validateField = (input, {show = true} = {}) => {
        if (!input.dataset.validate || !isActive(input)) {
            setError(input, '');
            return true;
        }

        const value = input.value || '';
        const rule = RULES[input.dataset.validate];
        const valid = RULES.required(value) && (!rule || rule(value));

        if (show || valid) {
            setError(input, valid ? '' : (i18n[input.dataset.message] || i18n.required));
        }

        return valid;
    };

    // Validation runs when leaving a field, not while typing; while typing an existing error disappears once fixed.
    form.addEventListener('focusout', (event) => {
        const input = event.target;
        if (!input.dataset || !input.dataset.validate) {
            return;
        }
        if (input.value === '' && !state.touched.has(input.name)) {
            return;
        }
        validateField(input);
    });

    /* Masks: phone "600 700 800", postcode "00-000", NIP digits ---------------------------------- */

    const reformat = (input, format) => {
        const caret = input.selectionStart || 0;
        const digitsBefore = digits(input.value.slice(0, caret)).length;
        const formatted = format(input.value);

        if (formatted === input.value) {
            return;
        }

        input.value = formatted;

        let position = 0;
        let count = 0;
        while (position < formatted.length && count < digitsBefore) {
            if (/\d/.test(formatted[position])) {
                count++;
            }
            position++;
        }
        if (document.activeElement === input) {
            input.setSelectionRange(position, position);
        }
    };

    const masks = {
        billing_phone: formatPhone,
        billing_postcode: formatPostcode,
        wbs_invoice_postcode: formatPostcode,
        vat_number: (value) => digits(value).slice(0, 10),
    };

    form.addEventListener('input', (event) => {
        const input = event.target;

        if (masks[input.id] && !(event.inputType || '').startsWith('delete')) {
            reformat(input, masks[input.id]);
        }

        if (input.dataset && input.dataset.validate) {
            state.touched.add(input.name);
            if (input.getAttribute('aria-invalid') === 'true') {
                validateField(input, {show: false});
            }
        }

        if (input.id === 'billing_postcode' && RULES.postcode(input.value)) {
            onPostcode();
        }

        if (input.dataset && input.dataset.counter) {
            updateCounter(input);
        }
    });

    Object.keys(masks).forEach((id) => {
        const input = byId(id);
        if (input && input.value) {
            input.value = masks[id](input.value);
        }
    });

    /* Postcode → city suggestions, nearest parcel locker ------------------------------------------ */

    const postcodeInput = byId('billing_postcode');
    const cityInput = byId('billing_city');
    const cityLookups = new Map();

    const lookupCity = async (postcode) => {
        if (!cityLookups.has(postcode)) {
            cityLookups.set(postcode, post('wbs_postcode_lookup', {security: config().checkoutNonce, postcode})
                .then((response) => (response && response.success && response.data.cities) || [])
                .catch(() => []));
        }

        try {
            const cities = await cityLookups.get(postcode);
            if (!postcodeInput || postcodeInput.value.trim() !== postcode) {
                return;
            }
            const list = byId('billing_city_suggestions');

            if (list) {
                list.innerHTML = '';
                cities.forEach((city) => {
                    const option = document.createElement('option');
                    option.value = city;
                    list.appendChild(option);
                });
            }

            if (cities.length === 1 && cityInput && !cityInput.value.trim()) {
                cityInput.value = cities[0];
                validateField(cityInput, {show: false});
                saveDraftSoon();
            }
        } catch (e) {
            // Suggestions are optional.
        }
    };

    const onPostcode = () => {
        const postcode = postcodeInput ? postcodeInput.value.trim() : '';
        if (!RULES.postcode(postcode)) {
            return;
        }
        lookupCity(postcode);
        suggestPoint();
    };

    if (postcodeInput) {
        postcodeInput.addEventListener('change', () => {
            onPostcode();
            $body.trigger('update_checkout');
        });
    }

    /* Delivery: tiles and the pickup point bar ----------------------------------------------------- */

    const pickupBar = root.querySelector('[data-wbs-pickup]');
    const shippingError = byId('wbs-shipping-error');

    const pickupTypes = {
        inpost: {
            select: () => byId('paczkomat_id'),
            label: () => byId('wbs_pickup_label_inpost'),
            open: () => {
                if (!window.easyPack || !byId('open-geowidget')) {
                    return false;
                }
                $('#open-geowidget').trigger('click');
                return true;
            },
        },
        orlen: {
            select: () => byId('paczka_w_ruchu_punkt_odbioru'),
            label: () => byId('wbs_pickup_label_orlen'),
            open: () => {
                const opener = $('#op-select-from-map');
                if (!opener.length || (!window.RuchWidget && !$.fn.pwrgeopicker)) {
                    return false;
                }
                const postcode = postcodeInput ? postcodeInput.value : '';
                const city = cityInput ? cityInput.value : '';
                opener.attr('data-initial_postcode', postcode).data('initial_postcode', postcode);
                opener.attr('data-initial_city', city).data('initial_city', city);
                opener.trigger('click');
                return true;
            },
        },
    };

    const selectedShippingTile = () => {
        const input = root.querySelector('.wbs-tile--shipping .wbs-tile__radio:checked');
        return input ? input.closest('.wbs-tile') : null;
    };

    const pickupType = () => {
        const tile = selectedShippingTile();
        const type = tile ? tile.dataset.pickup : '';
        return type && pickupTypes[type] && pickupTypes[type].select() ? type : '';
    };

    const optionText = (select) => {
        const option = select && select.selectedIndex > -1 ? select.options[select.selectedIndex] : null;
        return option && option.value ? option.textContent.trim() : '';
    };

    const pointLabel = (type) => {
        const {select, label} = pickupTypes[type];
        return (label() && label().value) || optionText(select());
    };

    // A locker suggested from the postcode may be replaced when the postcode changes; one chosen by hand stays.
    const setPointAuto = (auto) => {
        state.pointAuto = auto;
        const flag = byId('wbs_pickup_auto');
        if (flag) {
            flag.value = auto ? '1' : '';
        }
    };

    const setShippingError = (message) => {
        if (shippingError) {
            shippingError.textContent = message;
        }
        if (pickupBar) {
            pickupBar.classList.toggle('is-invalid', !!message);
        }
    };

    const setPointHint = (message) => {
        const hint = pickupBar ? pickupBar.querySelector('[data-pickup-hint]') : null;
        if (hint) {
            hint.textContent = message;
            hint.hidden = !message;
        }
    };

    const renderPickup = () => {
        if (!pickupBar) {
            return;
        }

        const type = pickupType();
        const tile = selectedShippingTile();

        if (!type || !tile) {
            pickupBar.hidden = true;
            return;
        }

        const value = pickupTypes[type].select().value;

        pickupBar.hidden = false;
        pickupBar.style.order = String((parseInt(tile.style.order, 10) || 0) + 1);
        pickupBar.classList.toggle('is-empty', !value);
        pickupBar.querySelector('[data-pickup-title]').textContent = `${tile.dataset.pointLabel || ''}:`;
        pickupBar.querySelector('[data-pickup-value]').textContent = value ? pointLabel(type) || value : i18n.noPoint;
        pickupBar.querySelector('[data-pickup-change]').textContent = value ? i18n.changePoint : i18n.choosePoint;
    };

    const setPoint = (type, id, label, {auto = false} = {}) => {
        const select = pickupTypes[type].select();
        if (!select) {
            return;
        }

        let option = Array.prototype.find.call(select.options, (item) => item.value === id);
        if (!option) {
            option = new Option(label || id, id, true, true);
            select.add(option);
        }
        option.textContent = label || id;

        state.settingPoint = true;
        $(select).val(id).trigger('change');
        state.settingPoint = false;

        pickupTypes[type].label().value = label || id;
        if (type === 'inpost') {
            setPointAuto(auto);
        }

        setShippingError('');
        renderPickup();
        saveDraftSoon();
    };

    // Point chosen in a plugin's map widget (they set the value and trigger "change" through jQuery).
    $(document).on('change', '#paczkomat_id, #paczka_w_ruchu_punkt_odbioru', function () {
        if (state.settingPoint) {
            return;
        }

        const type = this.id === 'paczkomat_id' ? 'inpost' : 'orlen';
        const text = optionText(this);

        pickupTypes[type].label().value = type === 'inpost' ? formatInpostLabel(text, this.value) : text;
        if (type === 'inpost') {
            setPointAuto(false);
        }

        setPointHint('');
        setShippingError('');
        renderPickup();
        saveDraftSoon();
    });

    const suggestPoint = async () => {
        if (pickupType() !== 'inpost' || !postcodeInput) {
            return;
        }

        const select = pickupTypes.inpost.select();
        const postcode = postcodeInput.value.trim();

        if ((select.value && !state.pointAuto) || !RULES.postcode(postcode) || postcode === state.pointPostcode) {
            return;
        }
        state.pointPostcode = postcode;

        try {
            const response = await post('wbs_nearest_point', {security: config().checkoutNonce, postcode});
            const point = response && response.success ? response.data.point : null;

            if (point && pickupType() === 'inpost' && (!select.value || state.pointAuto)) {
                setPoint('inpost', point.id, point.label, {auto: true});
                setPointHint(sprintf(i18n.pointSuggested, postcode));
            }
        } catch (e) {
            // The customer can still choose on the map.
        }
    };

    const showPointList = (type) => {
        const wrapper = root.querySelector('.wbs-pickup-fields');
        if (!wrapper) {
            return;
        }
        wrapper.hidden = false;
        wrapper.dataset.type = type;
        setPointHint(i18n.mapUnavailable);
    };

    root.addEventListener('click', (event) => {
        if (!event.target.closest('[data-pickup-change]')) {
            return;
        }
        event.preventDefault();

        const type = pickupType();
        if (type && !pickupTypes[type].open()) {
            showPointList(type);
        }
    });

    const syncTiles = (selector) => {
        root.querySelectorAll(selector).forEach((tile) => {
            const input = tile.querySelector('.wbs-tile__radio');
            tile.classList.toggle('is-selected', !!(input && input.checked));
        });
    };

    /* Payment methods: wallets available only on supporting devices, BLIK code in the last step ----- */

    const walletSupported = (kind) => {
        let apple = false;
        try {
            apple = !!(window.ApplePaySession && window.ApplePaySession.canMakePayments());
        } catch (e) {
            apple = false;
        }
        const google = 'PaymentRequest' in window;

        if (kind === 'apple') {
            return apple;
        }
        if (kind === 'google') {
            return google;
        }
        return apple || google;
    };

    const applyWallets = () => {
        root.querySelectorAll('.wbs-tile--payment[data-wallet]').forEach((tile) => {
            const supported = walletSupported(tile.dataset.wallet);
            const input = tile.querySelector('input[name="payment_method"]');

            tile.hidden = !supported;

            if (!supported && input && input.checked) {
                const first = root.querySelector('.wbs-tile--payment:not([hidden]) input[name="payment_method"]');
                if (first) {
                    $(first).prop('checked', true).trigger('click');
                }
            }
        });
    };

    const selectedPaymentTile = () => {
        const input = root.querySelector('input[name="payment_method"]:checked');
        return input ? input.closest('.wbs-tile--payment') : null;
    };

    const CHANNEL_NAMES = {gpay: 'Google Pay', googlepay: 'Google Pay', applepay: 'Apple Pay', blik: 'BLIK', card: 'Karta płatnicza'};

    /**
     * Kanał wybrany wewnątrz metody płatności (Google Pay albo Apple Pay, bank z listy) - trafia do podsumowania,
     * żeby klient widział dokładnie to, co kliknął.
     */
    const selectedChannel = (tile) => {
        const radio = tile ? tile.querySelector('.payment_box input[type="radio"]:checked') : null;
        if (!radio) {
            return '';
        }

        const item = radio.closest('li') || radio.parentElement;
        const label = radio.closest('label');
        const image = label ? label.querySelector('img') : null;
        const candidates = [item ? item.getAttribute('title') : '', image ? image.getAttribute('alt') : ''];
        const readable = candidates
            .map((text) => (text || '').trim())
            .find((text) => text && text.length <= 40 && !/niedost|unavailable|not available|logo/i.test(text));

        if (readable) {
            return readable;
        }

        const key = String(radio.value || '').split('-').pop().toLowerCase();
        return CHANNEL_NAMES[key] || String(radio.value || '').replace(/[-_]/g, ' ');
    };

    /**
     * Gateways with their own choice inside the tile (PayU bank list, imoje channels) - returns the first radio of a
     * group with nothing picked. The gateway checks it only on "Zamawiam i płacę", so it is asked for in step 2.
     */
    const unansweredChoice = (tile) => {
        const box = tile ? tile.querySelector('.payment_box') : null;
        const radios = box ? Array.from(box.querySelectorAll('input[type="radio"]:not(:disabled)')) : [];
        const names = [...new Set(radios.map((radio) => radio.name).filter(Boolean))];
        const name = names.find((group) => !radios.some((radio) => radio.name === group && radio.checked));

        return name ? radios.find((radio) => radio.name === name) : null;
    };

    let choiceMessage = null;

    // Message shown inside the selected payment tile, right above the gateway's own fields.
    const setChoiceError = (tile, message) => {
        const box = tile ? tile.querySelector('.payment_box') || tile : null;

        if (choiceMessage && (!message || !box || !box.contains(choiceMessage))) {
            choiceMessage.remove();
            choiceMessage = null;
        }
        if (!message || !box) {
            return;
        }
        if (!choiceMessage) {
            choiceMessage = document.createElement('p');
            choiceMessage.className = 'wbs-field__error wbs-tile__error';
            choiceMessage.setAttribute('role', 'alert');
            box.prepend(choiceMessage);
        }
        choiceMessage.textContent = message;
    };

    const gatewayBlikInput = () => {
        const tile = selectedPaymentTile();
        return tile ? tile.querySelector('input[name="imoje-blik-code"]') : null;
    };

    // The BLIK code is valid ~2 minutes - it is entered in step 3, right before "Zamawiam i płacę".
    const moveBlikCode = () => {
        root.querySelectorAll('.wbs-payment input[name="imoje-blik-code"]').forEach((input) => {
            const container = input.closest('.imoje-blik-code-container') || input.parentElement;
            if (container.classList.contains('wbs-blik-moved')) {
                return;
            }
            container.classList.add('wbs-blik-moved');
            const note = document.createElement('p');
            note.className = 'wbs-blik-note';
            note.textContent = i18n.blikLater;
            container.parentNode.insertBefore(note, container);
        });
    };

    const blikProxy = () => {
        const wrapper = root.querySelector('[data-review="blik"]');
        return wrapper && !wrapper.hidden ? byId('wbs_blik_code') : null;
    };

    /* Invoice section ----------------------------------------------------------------------------- */

    const toggleInvoice = () => {
        const invoice = byId('billing_invoice_checkbox');
        const other = byId('wbs_invoice_other_address');
        const group = byId('wbs-invoice');
        const address = byId('wbs-invoice-address');

        if (invoice && group) {
            group.hidden = !invoice.checked;
            invoice.setAttribute('aria-expanded', invoice.checked ? 'true' : 'false');
        }
        if (other && address) {
            address.hidden = !invoiceAddressOn();
            other.setAttribute('aria-expanded', other.checked ? 'true' : 'false');
        }

        root.querySelectorAll('[data-when]').forEach((input) => {
            if (!isActive(input)) {
                setError(input, '');
            }
        });
    };

    /* Order notes counter ------------------------------------------------------------------------- */

    const updateCounter = (textarea) => {
        const counter = byId(textarea.dataset.counter);
        const limit = parseInt(textarea.getAttribute('maxlength'), 10) || 0;
        if (counter && limit) {
            counter.textContent = sprintf(i18n.charsLeft, Math.max(0, limit - textarea.value.length));
        }
    };

    root.querySelectorAll('[data-counter]').forEach(updateCounter);

    /* Steps --------------------------------------------------------------------------------------- */

    const headerOffset = () => {
        const header = byId('masthead');
        return (header ? header.getBoundingClientRect().height : 0) + 16;
    };

    const scrollToElement = (element) => {
        const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset();
        window.scrollTo({top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth'});
    };

    const scrollToInvalid = (element) => {
        scrollToElement(element.closest('.wbs-field, .form-row, .wbs-pickup, .wbs-tile, .wbs-card') || element);
        const focusTarget = element.matches('input, select, textarea, button') ? element : element.querySelector('button, input');
        if (focusTarget) {
            focusTarget.focus({preventScroll: true});
        }
    };

    /**
     * @return {Element|null} first invalid element of the step (null when the step is valid)
     */
    const validateStep = (step, {show = true} = {}) => {
        const panel = root.querySelector(`[data-step-panel="${step}"]`);
        let invalid = null;

        if (!panel) {
            return null;
        }

        if (step === 1) {
            panel.querySelectorAll('input[data-validate]').forEach((input) => {
                if (!validateField(input, {show}) && !invalid) {
                    invalid = input;
                }
            });

            if (panel.querySelector('.wbs-shipping')) {
                const chosen = panel.querySelector('input[name^="shipping_method"]:checked, input[name^="shipping_method"][type="hidden"]');
                const type = pickupType();
                let message = '';

                if (!chosen) {
                    message = i18n.shipping;
                } else if (type && !pickupTypes[type].select().value) {
                    message = i18n.point;
                }

                if (show) {
                    setShippingError(message);
                }
                if (message && !invalid) {
                    invalid = type ? pickupBar : panel.querySelector('.wbs-shipping');
                }
            }
        }

        if (step === 2) {
            const methods = panel.querySelectorAll('input[name="payment_method"]');
            const paymentError = byId('wbs-payment-error');
            const missingPayment = methods.length > 0 && !panel.querySelector('input[name="payment_method"]:checked');

            const choice = missingPayment ? null : unansweredChoice(selectedPaymentTile());

            if (show && paymentError) {
                paymentError.textContent = missingPayment ? i18n.payment : '';
            }
            if (show) {
                setChoiceError(choice ? selectedPaymentTile() : null, choice ? i18n.bank : '');
            }
            if (missingPayment) {
                invalid = panel.querySelector('.wbs-payment-card');
            } else if (choice) {
                invalid = choice;
            }

            const terms = byId('terms');
            if (terms && !terms.checked) {
                if (show) {
                    setError(terms, i18n.terms);
                }
                invalid = invalid || terms;
            } else if (terms && show) {
                setError(terms, '');
            }
        }

        if (step === 3) {
            const blik = blikProxy();
            if (blik && !/^\d{6}$/.test(digits(blik.value))) {
                if (show) {
                    setError(blik, i18n.blik);
                }
                invalid = blik;
            }
        }

        return invalid;
    };

    const updateStepper = () => {
        root.querySelectorAll('[data-step-item]').forEach((item) => {
            const number = Number(item.dataset.stepItem);
            const button = item.querySelector('button');
            const current = number === state.step;
            const done = state.completed.has(number) && !current;
            let reachable = true;

            for (let previous = 1; previous < number; previous++) {
                if (!state.completed.has(previous)) {
                    reachable = false;
                }
            }

            item.classList.toggle('is-current', current);
            item.classList.toggle('is-done', done);
            item.classList.toggle('is-reached', current || done || number < state.step);
            button.disabled = !reachable && !current;

            if (current) {
                button.setAttribute('aria-current', 'step');
            } else {
                button.removeAttribute('aria-current');
            }

            const status = item.querySelector('.wbs-stepper__state');
            if (status) {
                status.textContent = current ? '(bieżący)' : done ? '(ukończony)' : '';
            }
        });
    };

    const placeOrderButton = () => byId('place_order');
    const stickyButton = root.querySelector('[data-sticky-action]');

    const updateLabels = () => {
        const next = root.querySelector('[data-next="2"]');
        if (next) {
            next.textContent = state.returnToSummary ? i18n.saveAndReturn : i18n.nextPayment;
        }

        if (stickyButton && !state.busy) {
            stickyButton.textContent = state.step === 1 ? i18n.next : state.step === 2 ? i18n.summary : i18n.placeOrder;
        }

        const toSummary = root.querySelector('[data-next="3"]');
        const terms = byId('terms');
        if (toSummary && terms) {
            toSummary.setAttribute('aria-disabled', terms.checked ? 'false' : 'true');
        }
    };

    const reviewText = (key, text) => {
        const element = root.querySelector(`[data-review="${key}"]`);
        if (element) {
            element.textContent = text;
        }
    };

    const copyLogo = (tile, key) => {
        const target = root.querySelector(`[data-review="${key}"]`);
        const template = tile.querySelector('template[data-review-logo]');
        if (target) {
            target.innerHTML = template ? template.innerHTML : '';
        }
    };

    const value = (id) => (byId(id) ? byId(id).value.trim() : '');

    const renderReview = () => {
        const phone = phoneDigits(value('billing_phone'));

        reviewText('name', `${value('billing_first_name')} ${value('billing_last_name')}`.trim());
        reviewText('address', [value('billing_address_1'), `${value('billing_postcode')} ${value('billing_city')}`.trim()].filter(Boolean).join(', '));
        reviewText('contact', [value('billing_email'), phone ? `${i18n.tel} ${phone.length === 9 ? groupDigits(phone) : value('billing_phone')}` : ''].filter(Boolean).join(' | '));

        const invoice = root.querySelector('[data-review="invoice"]');
        if (invoice) {
            invoice.hidden = !invoiceOn();
            if (invoiceOn()) {
                let text = `${i18n.invoice} ${value('billing_company')}, NIP ${digits(value('vat_number'))}`;
                if (invoiceAddressOn()) {
                    text += `, ${value('wbs_invoice_address_1')}, ${value('wbs_invoice_postcode')} ${value('wbs_invoice_city')}`;
                }
                invoice.textContent = text;
            }
        }

        const tile = selectedShippingTile();
        if (tile) {
            const type = pickupType();
            const label = type ? pointLabel(type) : '';
            const name = tile.querySelector('[data-review-name]').textContent.trim();
            const price = tile.querySelector('[data-review-price]').textContent.replace(/\s+/g, ' ').trim();

            reviewText('carrier', `${name}${label ? ` - ${label.replace(' - ', ', ')}` : ''} | ${price}`);
            reviewText('date', tile.querySelector('[data-review-date]').textContent.trim());
            copyLogo(tile, 'carrier-logo');
        }

        const payment = selectedPaymentTile();
        if (payment) {
            const name = payment.querySelector('[data-review-name]').textContent.trim();
            const channel = selectedChannel(payment);

            // Przy portfelach nazwa kafelka to "Google Pay / Apple Pay", więc sam kanał mówi więcej niż oba naraz.
            reviewText('payment', channel ? (payment.dataset.wallet ? channel : `${name} · ${channel}`) : name);
            copyLogo(payment, 'payment-logo');
        }

        const blik = root.querySelector('[data-review="blik"]');
        if (blik) {
            blik.hidden = !gatewayBlikInput();
        }
    };

    const show = (step, {push = true, focus = true} = {}) => {
        const changed = step !== state.step;

        state.step = step;
        root.dataset.step = String(step);

        root.querySelectorAll('[data-step-panel]').forEach((panel) => {
            panel.hidden = Number(panel.dataset.stepPanel) !== step;
        });

        if (step === 3) {
            renderReview();
        }

        updateStepper();
        updateLabels();

        if (changed && push) {
            window.history.pushState({wbsStep: step}, '', `#krok-${step}`);
        }

        if (changed && focus) {
            scrollToElement(root);
            const panel = root.querySelector(`[data-step-panel="${step}"]`);
            if (panel) {
                panel.focus({preventScroll: true});
            }
        }
    };

    /**
     * Moving forward requires every earlier step to be valid - otherwise the first invalid step is shown.
     */
    const goTo = (target, {push = true, silent = false} = {}) => {
        for (let step = 1; step < target; step++) {
            const invalid = validateStep(step, {show: !silent});

            if (invalid) {
                state.completed.delete(step);
                show(step, {push, focus: silent});
                if (!silent) {
                    scrollToInvalid(invalid);
                }
                return false;
            }

            state.completed.add(step);
        }

        show(target, {push});
        return true;
    };

    /* Analityka ----------------------------------------------------------------------------------
     * Wtyczka Google Tag Managera wysyła view_cart, begin_checkout i purchase. Kroki kasy dzieją się
     * bez przeładowania strony, więc wybór dostawy i płatności zgłaszamy stąd.
     */

    const tracked = {add_shipping_info: '', add_payment_info: ''};

    const trackStep = (event, tile, key) => {
        const data = config().analytics;
        const name = tile ? (tile.querySelector('[data-review-name]') || {}).textContent : '';
        const value = (name || '').trim();

        if (!data || !data.items || !data.items.length || !value || tracked[event] === value) {
            return;
        }

        tracked[event] = value;
        trackEvent({
            event,
            ecommerce: Object.assign({currency: data.currency, value: data.value, items: data.items}, {[key]: value}),
        });
    };

    const next = (target) => {
        const from = state.step;

        if (state.step === 1 && state.returnToSummary) {
            target = 3;
        }
        if (goTo(target)) {
            if (from === 1) {
                trackStep('add_shipping_info', selectedShippingTile(), 'shipping_tier');
            }
            if (from === 2) {
                trackStep('add_payment_info', selectedPaymentTile(), 'payment_type');
            }
            if (target === 3) {
                state.returnToSummary = false;
                updateLabels();
            }
        }
        saveDraftSoon();
    };

    root.addEventListener('click', (event) => {
        const nextButton = event.target.closest('[data-next]');
        if (nextButton) {
            event.preventDefault();
            next(Number(nextButton.dataset.next));
            return;
        }

        const gotoButton = event.target.closest('[data-goto]');
        if (gotoButton && !gotoButton.disabled) {
            event.preventDefault();
            state.returnToSummary = false;
            goTo(Number(gotoButton.dataset.goto));
            return;
        }

        const edit = event.target.closest('[data-edit]');
        if (edit) {
            if (edit.dataset.edit === 'cart') {
                try {
                    sessionStorage.setItem(RETURN_KEY, JSON.stringify({time: Date.now()}));
                } catch (e) {
                    // Without storage the customer simply starts from step 1 (data is kept).
                }
                return;
            }
            event.preventDefault();
            state.returnToSummary = true;
            show(Number(edit.dataset.edit));
            return;
        }

        if (event.target.closest('[data-sticky-action]')) {
            event.preventDefault();
            if (state.step === 3) {
                const button = placeOrderButton();
                if (button && !state.busy) {
                    button.click();
                }
            } else {
                next(state.step + 1);
            }
        }
    });

    // Enter in steps 1-2 moves to the next step instead of submitting the order.
    form.addEventListener('keydown', (event) => {
        const target = event.target;
        if (event.key !== 'Enter' || target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON' || target.type === 'submit') {
            return;
        }
        const panel = target.closest('[data-step-panel]');
        const step = panel ? Number(panel.dataset.stepPanel) : 3;
        if (step < 3) {
            event.preventDefault();
            next(step + 1);
        }
    });

    window.addEventListener('popstate', (event) => {
        const fromHash = (window.location.hash.match(/^#krok-([123])$/) || [])[1];
        const step = (event.state && event.state.wbsStep) || Number(fromHash) || 1;
        goTo(step, {push: false, silent: true});
    });

    form.addEventListener('change', (event) => {
        const target = event.target;

        if (target.matches('input[name^="shipping_method"]')) {
            syncTiles('.wbs-tile--shipping');
            setShippingError('');
            setPointHint('');
            renderPickup();
            suggestPoint();
        }

        if (target.matches('input[name="payment_method"]')) {
            syncTiles('.wbs-tile--payment');
            const paymentError = byId('wbs-payment-error');
            if (paymentError) {
                paymentError.textContent = '';
            }
            setChoiceError(null, '');
        }

        if (target.type === 'radio' && target.closest('.payment_box')) {
            setChoiceError(null, '');
        }

        if (target.id === 'terms') {
            if (target.checked) {
                setError(target, '');
            }
            updateLabels();
        }

        if (target.id === 'billing_invoice_checkbox' || target.id === 'wbs_invoice_other_address') {
            toggleInvoice();
        }

        if (target.id === 'billing_city' || target.id === 'billing_address_1') {
            $body.trigger('update_checkout');
        }

        saveDraftSoon();
    });

    /* Submit ------------------------------------------------------------------------------------- */

    const setBusy = (busy) => {
        const button = placeOrderButton();
        state.busy = busy;

        [button, stickyButton].forEach((element) => {
            if (!element) {
                return;
            }
            element.classList.toggle('is-loading', busy);
            element.setAttribute('aria-busy', busy ? 'true' : 'false');
        });

        if (button) {
            if (busy) {
                button.dataset.label = button.textContent;
                // Disabled only after the submit event has been dispatched.
                setTimeout(() => {
                    button.disabled = true;
                    button.textContent = i18n.processing;
                }, 0);
            } else {
                button.disabled = false;
                button.textContent = button.dataset.value || button.dataset.label || i18n.placeOrder;
            }
        }
        if (stickyButton) {
            stickyButton.disabled = busy;
            stickyButton.textContent = busy ? i18n.processing : i18n.placeOrder;
        }
    };

    form.addEventListener('click', (event) => {
        if (!event.target.closest('#place_order')) {
            return;
        }

        if (state.busy) {
            event.preventDefault();
            return;
        }

        for (let step = 1; step <= 3; step++) {
            const invalid = validateStep(step);
            if (invalid) {
                event.preventDefault();
                if (step !== state.step) {
                    show(step, {focus: false});
                }
                scrollToInvalid(invalid);
                return;
            }
            state.completed.add(step);
        }

        const gatewayInput = gatewayBlikInput();
        const proxy = byId('wbs_blik_code');
        if (gatewayInput && proxy) {
            gatewayInput.value = digits(proxy.value);
        }

        setBusy(true);
    });

    // Gateways validate their own fields in "checkout_place_order_{id}" (bank list, card form) and may stop the
    // submission without any "checkout_error" - WooCommerce then never marks the form as processing. The button must
    // not stay in "Przetwarzanie…" and the gateway's message lives in step 2, so that step is shown.
    $(form).on('submit', () => {
        setTimeout(() => {
            if (form.classList.contains('processing')) {
                if (!state.busy) {
                    setBusy(true);
                }
                return;
            }
            if (!state.busy) {
                return;
            }

            setBusy(false);

            const tile = selectedPaymentTile();
            const choice = unansweredChoice(tile);
            show(2, {focus: false});
            setChoiceError(tile, choice ? i18n.bank : i18n.paymentBlocked);
            if (tile) {
                scrollToElement(tile);
                const focusTarget = choice || tile.querySelector('input[name="payment_method"]');
                if (focusTarget) {
                    focusTarget.focus({preventScroll: true});
                }
            }
        }, 0);
    });

    $body.on('checkout_error', () => {
        setBusy(false);

        // Right after this event WooCommerce adds its own copy of each message under the field and points the field's
        // aria-describedby at it. The message is already in the field's error slot, so the copy is dropped and the
        // original description (hint + error) restored.
        const described = new Map();
        root.querySelectorAll('[aria-describedby]').forEach((field) => described.set(field, field.getAttribute('aria-describedby')));
        setTimeout(() => {
            root.querySelectorAll('.checkout-inline-error-message').forEach((message) => message.remove());
            described.forEach((value, field) => field.setAttribute('aria-describedby', value));
        }, 0);

        const group = document.querySelector('.woocommerce-NoticeGroup-checkout');
        const items = group ? Array.from(group.querySelectorAll('li')) : [];
        let targetStep = null;
        let firstField = null;

        items.forEach((item) => {
            const text = item.textContent.trim();
            const field = item.dataset.id ? byId(item.dataset.id) : null;
            let step = null;

            if (field) {
                const panel = field.closest('[data-step-panel]');
                step = panel ? Number(panel.dataset.stepPanel) : null;
                if (field.matches('input, select, textarea')) {
                    setError(field, text);
                }
                firstField = firstField || field;
            } else if (/paczkomat|punkt|dostaw|wysyłk|nr domu/i.test(text)) {
                step = 1;
                setShippingError(/paczkomat|punkt/i.test(text) ? text : '');
            } else if (/blik/i.test(text)) {
                step = 3;
            } else if (/regulamin|warunk|terms/i.test(text)) {
                step = 2;
            } else if (/płatno|bank|payment|kart/i.test(text)) {
                step = 2;
            }

            if (step && (!targetStep || step < targetStep)) {
                targetStep = step;
            }
        });

        if (targetStep && targetStep !== state.step) {
            show(targetStep, {focus: false});
        }

        if (firstField && !firstField.closest('[hidden]')) {
            scrollToInvalid(firstField);
        } else if (group) {
            scrollToElement(group);
        }
    });

    /* Draft: entered data survives leaving the checkout (e.g. back to the cart) ------------------ */

    const saveDraft = () => {
        post('wbs_checkout_draft', {security: config().checkoutNonce, form: $(form).serialize()}).catch(() => {});
    };
    const saveDraftSoon = debounce(saveDraft, 1200);

    window.addEventListener('pagehide', () => {
        if (state.busy || !navigator.sendBeacon) {
            return;
        }
        const data = new FormData();
        data.append('security', config().checkoutNonce);
        data.append('form', $(form).serialize());
        navigator.sendBeacon(endpoint('wbs_checkout_draft'), data);
    });

    /* Login modal --------------------------------------------------------------------------------- */

    const dialog = byId('wbs-login-dialog');
    let dialogOpener = null;

    document.addEventListener('click', (event) => {
        if (!dialog) {
            return;
        }

        const open = event.target.closest('[data-login-open]');
        if (open) {
            dialogOpener = open;
            if (typeof dialog.showModal === 'function') {
                dialog.showModal();
            } else {
                dialog.setAttribute('open', '');
            }
            const first = dialog.querySelector('input:not([type="hidden"])');
            if (first) {
                first.focus();
            }
            return;
        }

        if (event.target.closest('[data-dialog-close]') || event.target === dialog) {
            if (typeof dialog.close === 'function') {
                dialog.close();
            } else {
                dialog.removeAttribute('open');
            }
        }
    });

    if (dialog) {
        dialog.addEventListener('close', () => {
            if (dialogOpener) {
                dialogOpener.focus();
            }
        });
    }

    /* WooCommerce refreshes -------------------------------------------------------------------- */

    $body.on('update_checkout', () => root.classList.add('is-updating'));

    $body.on('updated_checkout', () => {
        root.classList.remove('is-updating');
        syncTiles('.wbs-tile--shipping');
        syncTiles('.wbs-tile--payment');
        renderPickup();
        applyWallets();
        moveBlikCode();
        updateLabels();
        if (state.step === 3) {
            renderReview();
        }
    });

    /* Start ------------------------------------------------------------------------------------- */

    let initial = Number((window.location.hash.match(/^#krok-([123])$/) || [])[1]) || 1;

    try {
        const stored = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null');
        sessionStorage.removeItem(RETURN_KEY);
        if (stored && Date.now() - stored.time < RETURN_TTL) {
            initial = 3;
        }
    } catch (e) {
        // ignore
    }

    state.pointAuto = !!(byId('wbs_pickup_auto') && byId('wbs_pickup_auto').value === '1');

    toggleInvoice();
    renderPickup();
    applyWallets();
    moveBlikCode();

    window.history.replaceState({wbsStep: 1}, '', window.location.pathname + window.location.search);
    if (initial > 1) {
        goTo(initial, {push: true, silent: true});
    } else {
        updateStepper();
        updateLabels();
    }

    if (postcodeInput && RULES.postcode(postcodeInput.value)) {
        onPostcode();
    }
};
